import { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '@/config/api';

// Hub colors
const hubColors = ['#F97316', '#3B82F6', '#10B981', '#8B5CF6', '#EF4444', '#06B6D4', '#EC4899'];
export const getHubColor = (index) => hubColors[index % hubColors.length];

export const useSidebarData = (token, user, onChannelsLoaded, selectedChannel, onSelectChannel, locationPathname) => {
  const [hubs, setHubs] = useState([]);
  const [channels, setChannels] = useState([]);
  const [boards, setBoards] = useState([]);
  const [dmConversations, setDmConversations] = useState([]);
  const [expandedSections, setExpandedSections] = useState(['channels', 'whiteboards']);
  const [expandedHubs, setExpandedHubs] = useState([]);
  const [selectedHub, setSelectedHub] = useState({
    id: 'default',
    name: 'My Workspace',
    color: '#F97316'
  });
  const [userStatus, setUserStatus] = useState('online');

  // Fetch hubs
  const fetchHubs = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/hubs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHubs(data);
        if (data.length > 0) {
          setSelectedHub({ ...data[0], color: getHubColor(0) });
          // Auto-expand the first owned hub
          const ownedHubs = data.filter(h => h.owner_id === user?.id);
          if (ownedHubs.length > 0) {
            setExpandedHubs([ownedHubs[0].id]);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching hubs:', error);
    }
  }, [token, user?.id]);

  // Fetch channels
  const fetchChannels = useCallback(async (hubId = null) => {
    if (!token) return;
    try {
      const url = hubId 
        ? `${BACKEND_URL}/api/channels?hub_id=${hubId}`
        : `${BACKEND_URL}/api/channels`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setChannels(data);
        onChannelsLoaded?.(data);
        if (locationPathname === '/messages' && !selectedChannel && data.length > 0) {
          onSelectChannel?.(data[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching channels:', error);
    }
  }, [token, onChannelsLoaded, locationPathname, selectedChannel, onSelectChannel]);

  // Fetch boards
  const fetchBoards = useCallback(async (hubId = null) => {
    if (!token) return;
    try {
      const url = hubId 
        ? `${BACKEND_URL}/api/whiteboards?hub_id=${hubId}`
        : `${BACKEND_URL}/api/whiteboards`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBoards(data.slice(0, 8));
      }
    } catch (error) {
      console.error('Error fetching boards:', error);
    }
  }, [token]);

  // Fetch DM conversations
  const fetchDMConversations = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/dm/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDmConversations(data);
      }
    } catch (error) {
      console.error('Error fetching DM conversations:', error);
    }
  }, [token]);

  // Initial data fetch - wrapped to avoid lint warning
  useEffect(() => {
    const initializeData = async () => {
      await fetchHubs();
      await fetchChannels();
      await fetchBoards();
      await fetchDMConversations();
    };
    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Refetch content when hub changes
  useEffect(() => {
    const refetchHubContent = async () => {
      if (selectedHub?.id && selectedHub.id !== 'default') {
        await fetchChannels(selectedHub.id);
        await fetchBoards(selectedHub.id);
      } else {
        await fetchChannels();
        await fetchBoards();
      }
    };
    refetchHubContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHub?.id]);

  const toggleSection = (section) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const toggleHubExpanded = (hubId) => {
    setExpandedHubs(prev => 
      prev.includes(hubId) 
        ? [] 
        : [hubId]
    );
  };

  // Filter hubs into owned vs invited
  const ownedHubs = hubs.filter(h => h.owner_id === user?.id);
  const invitedHubs = hubs.filter(h => h.owner_id !== user?.id);

  return {
    // Data
    hubs,
    setHubs,
    channels,
    setChannels,
    boards,
    dmConversations,
    selectedHub,
    setSelectedHub,
    ownedHubs,
    invitedHubs,
    userStatus,
    setUserStatus,
    
    // Expansion state
    expandedSections,
    expandedHubs,
    
    // Actions
    fetchHubs,
    fetchChannels,
    fetchBoards,
    fetchDMConversations,
    toggleSection,
    toggleHubExpanded,
  };
};
